import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Wallet, Save, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface WalletSettings {
  address: string;
  network: string;
  currency: string;
}

const AdminWalletSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<WalletSettings>({
    address: "",
    network: "",
    currency: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("setting_key, setting_value")
        .in("setting_key", ["admin_wallet_address", "admin_wallet_network", "admin_wallet_currency"]);

      if (error) throw error;

      const settingsMap: Record<string, string> = {};
      data?.forEach((item) => {
        settingsMap[item.setting_key] = item.setting_value;
      });

      setSettings({
        address: settingsMap["admin_wallet_address"] || "",
        network: settingsMap["admin_wallet_network"] || "",
        currency: settingsMap["admin_wallet_currency"] || "",
      });
    } catch (error) {
      console.error("Error fetching wallet settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = [
        { key: "admin_wallet_address", value: settings.address },
        { key: "admin_wallet_network", value: settings.network },
        { key: "admin_wallet_currency", value: settings.currency },
      ];

      for (const update of updates) {
        const { error } = await supabase
          .from("platform_settings")
          .update({ setting_value: update.value })
          .eq("setting_key", update.key);

        if (error) throw error;
      }

      toast({
        title: "تم الحفظ",
        description: "تم تحديث إعدادات المحفظة بنجاح",
      });
    } catch (error) {
      console.error("Error saving wallet settings:", error);
      toast({
        title: "خطأ",
        description: "فشل في حفظ الإعدادات",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass">
        <CardContent className="p-6">
          <div className="animate-pulse text-muted-foreground">جاري التحميل...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Wallet className="w-5 h-5 text-primary" />
          إعدادات محفظة الأرباح
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Settings Display */}
        <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-crypto-green" />
            <span className="font-semibold text-foreground">الإعدادات الحالية</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">العنوان:</span>
              <span className="font-mono text-foreground text-xs break-all max-w-[200px] md:max-w-none" dir="ltr">
                {settings.address}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">الشبكة:</span>
              <span className="font-semibold text-foreground">{settings.network}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">العملة:</span>
              <span className="font-semibold text-primary">{settings.currency}</span>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-foreground">عنوان المحفظة</Label>
            <Input
              value={settings.address}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              className="bg-input border-border text-foreground font-mono text-sm"
              dir="ltr"
              placeholder="0x..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-foreground">الشبكة</Label>
              <Input
                value={settings.network}
                onChange={(e) => setSettings({ ...settings, network: e.target.value })}
                className="bg-input border-border text-foreground"
                placeholder="BEP20"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">العملة</Label>
              <Input
                value={settings.currency}
                onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                className="bg-input border-border text-foreground"
                placeholder="USDT"
              />
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full gradient-primary text-primary-foreground"
          >
            {saving ? (
              "جاري الحفظ..."
            ) : (
              <>
                <Save className="w-4 h-4 ml-2" />
                حفظ الإعدادات
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminWalletSettings;
